import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Plus, Trash2, Edit2, Save, X, Users,
  Radio, BookOpen, Loader2,
} from "lucide-react";

const TABS = [
  { id: "sermons", label: "Sermons", icon: BookOpen },
  { id: "live", label: "Live", icon: Radio },
  { id: "subscribers", label: "Subscribers", icon: Users },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sermons");

  useEffect(() => {
    if (!loading && (user === false || !user)) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) return (
    <div className="pt-32 ccn-container">
      <Loader2 className="w-8 h-8 animate-spin text-[#C8664D]" />
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-20 bg-white min-h-screen">
      <div className="ccn-container">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <div className="ccn-overline mb-2">Ministry control</div>
            <h1 className="ccn-h2">Welcome, {user.name || user.email}.</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn"
                  className="ccn-btn-ghost text-sm py-2.5">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="flex gap-2 border-b border-gray-200 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition ${
                tab === t.id ? "border-[#C8664D] text-[#C8664D]" : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "sermons" && <SermonsAdmin />}
        {tab === "live" && <LiveAdmin />}
        {tab === "subscribers" && <SubscribersAdmin />}
        {tab === "donations" && <DonationsAdmin />}
      </div>
    </div>
  );
}

function SermonsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", speaker: "", series: "", description: "", youtube_id: "", date: "", tags: "", featured: false };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/sermons");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string" ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : form.tags,
      };
      if (editing) {
        await api.patch(`/admin/sermons/${editing}`, payload);
        toast.success("Sermon updated");
      } else {
        await api.post("/admin/sermons", payload);
        toast.success("Sermon added");
      }
      setForm(blank);
      setEditing(null);
      await load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    try {
      await api.delete(`/admin/sermons/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ ...s, tags: (s.tags || []).join(", ") });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <form onSubmit={save} data-testid="sermon-form" className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 space-y-3 h-fit sticky top-28">
        <h3 className="font-heading text-xl mb-2">{editing ? "Edit sermon" : "Add new sermon"}</h3>
        {["title","speaker","series","youtube_id","date"].map((k) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-wide text-gray-500">{k.replace("_"," ")}</label>
            <input
              value={form[k] || ""} required={k === "title" || k === "speaker" || k === "youtube_id"}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`sermon-input-${k}`}
              className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]"
              placeholder={k === "youtube_id" ? "e.g. dQw4w9WgXcQ" : k === "date" ? "YYYY-MM-DD" : ""}
            />
          </div>
        ))}
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500">Tags (comma-separated)</label>
          <input value={form.tags || ""} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                 data-testid="sermon-input-tags"
                 className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-500">Description</label>
          <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3} data-testid="sermon-input-description"
                    className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
          Featured on home
        </label>
        <div className="flex gap-2 pt-2">
          <button type="submit" data-testid="sermon-save-btn" className="ccn-btn-primary flex-1 justify-center text-sm py-2.5">
            <Save className="w-4 h-4" /> {editing ? "Save changes" : "Add sermon"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="ccn-btn-ghost text-sm py-2.5">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-3 space-y-3" data-testid="sermons-list">
        {items.length === 0 && <div className="text-gray-500">No sermons yet.</div>}
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 flex gap-4 items-center" data-testid={`sermon-row-${s.id}`}>
            <img src={`https://img.youtube.com/vi/${s.youtube_id}/mqdefault.jpg`} alt="" className="w-32 h-20 object-cover rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg truncate">{s.title}</div>
              <div className="text-sm text-gray-500">{s.speaker} · {s.series || "—"} · {s.date}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(s)} className="p-2 rounded-lg hover:bg-gray-100" data-testid={`edit-sermon-${s.id}`}><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => remove(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`delete-sermon-${s.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveAdmin() {
  const [cfg, setCfg] = useState({ youtube_live_id: "", facebook_live_url: "", watch_live_url: "", is_live: false, title: "", schedule_note: "" });
  useEffect(() => { (async () => {
    const { data } = await api.get("/live");
    setCfg(data);
  })(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/live", cfg);
      toast.success("Live stream updated");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  return (
    <form onSubmit={save} className="max-w-2xl bg-white rounded-xl border border-gray-200 p-6 space-y-4" data-testid="live-form">
      <h3 className="font-heading text-xl">Live stream configuration</h3>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!cfg.is_live} onChange={(e) => setCfg({ ...cfg, is_live: e.target.checked })} data-testid="live-is-live" />
        Currently live
      </label>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-500">Watch Live button URL</label>
        <input value={cfg.watch_live_url || ""} onChange={(e) => setCfg({ ...cfg, watch_live_url: e.target.value })}
               data-testid="live-watch-url"
               placeholder="https://www.facebook.com/share/v/..."
               className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
        <div className="text-xs text-gray-500 mt-1">Where the homepage "Watch Live" button takes visitors. Paste a fresh Facebook/YouTube share URL each week. Leave empty to fall back to the /live page.</div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-500">YouTube Live Video ID</label>
        <input value={cfg.youtube_live_id || ""} onChange={(e) => setCfg({ ...cfg, youtube_live_id: e.target.value })}
               data-testid="live-youtube-id"
               placeholder="e.g. abc123XYZ"
               className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
        <div className="text-xs text-gray-500 mt-1">Find this in the YouTube live URL: ?v=THIS_PART</div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-500">Facebook Live embed code or URL</label>
        <textarea value={cfg.facebook_live_url || ""} onChange={(e) => setCfg({ ...cfg, facebook_live_url: e.target.value })}
               data-testid="live-facebook-url"
               rows={3}
               placeholder='Paste either the full <iframe ...> embed code from Facebook Share > Embed, OR a canonical video URL like https://www.facebook.com/yourpage/videos/1234567890'
               className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D] font-mono text-xs" />
        <div className="text-xs text-gray-500 mt-1">
          Tip: on a live Facebook video, click <strong>Share → Embed</strong> and paste the whole code here. Short share/v/ links won't embed — use the canonical video URL or the embed code instead.
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-500">Service title</label>
        <input value={cfg.title || ""} onChange={(e) => setCfg({ ...cfg, title: e.target.value })}
               data-testid="live-title"
               className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-500">Schedule note</label>
        <input value={cfg.schedule_note || ""} onChange={(e) => setCfg({ ...cfg, schedule_note: e.target.value })}
               data-testid="live-schedule"
               className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#C8664D]" />
      </div>
      <button type="submit" data-testid="live-save-btn" className="ccn-btn-primary text-sm py-2.5">
        <Save className="w-4 h-4" /> Save changes
      </button>
    </form>
  );
}

function SubscribersAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/subscribers"); setItems(data); } catch {}
  })(); }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="subscribers-table">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-heading text-xl">Subscribers · {items.length}</h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-6 py-3 font-medium text-gray-600">Email</th>
            <th className="text-left px-6 py-3 font-medium text-gray-600">Name</th>
            <th className="text-left px-6 py-3 font-medium text-gray-600">Joined</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="3" className="px-6 py-8 text-center text-gray-500">No subscribers yet.</td></tr>}
          {items.map((s) => (
            <tr key={s.id} className="border-t border-gray-100">
              <td className="px-6 py-3">{s.email}</td>
              <td className="px-6 py-3 text-gray-600">{s.name || "—"}</td>
              <td className="px-6 py-3 text-gray-500">{new Date(s.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DonationsAdmin() { return null; }
DonationsAdmin; // unused — left here to avoid breaking any external import


